// Card details for each project. The write-up itself lives in the matching .md file.
import proxmoxHomeLab from './proxmox-home-lab.md';
import windowsAdLab from './windows-ad-lab.md';
import azureEntraIdLabs from './azure-entra-id-labs.md';
import minecraftBedrockServer from './minecraft-bedrock-server.md';
import minecraftWorldMap from './minecraft-world-map.md';
import minecraftServerDashboard from './minecraft-server-dashboard.md';
import photoGallery from './photo-gallery.md';
import browserDevEnvironment from './browser-dev-environment.md';
import portfolioSite from './portfolio-site.md';

// A diagram too tall to show inline, so it gets its own page linked from the write-up.
export interface Diagram {
  slug: string;
  title: string;
  description: string;
  src: string;
  alt: string;
}

export interface Project {
  slug: string;
  title: string;
  summary: string;
  tags: string[];
  status: 'Complete' | 'In Progress' | 'Planned';
  featured?: boolean;
  content: string; // URL of the bundled .md file
  diagrams?: Diagram[];
  parent?: string; // slug of the project this one is part of; sub-projects get no homepage card
}

export const projects: Project[] = [
  {
    slug: 'proxmox-home-lab',
    title: 'Proxmox Home Lab and Secure Remote Access',
    summary:
      'A repurposed gaming PC running Proxmox VE 9 that hosts my lab and self-hosted services. Two network bridges keep the Windows lab on its own isolated network, and remote access goes through Cloudflare Tunnel behind Cloudflare Access instead of a VPN or open inbound ports.',
    tags: ['Proxmox VE', 'LXC', 'LVM-thin', 'Linux bridges', 'iptables NAT', 'Cloudflare Tunnel', 'Cloudflare Access'],
    status: 'Complete',
    featured: true,
    content: proxmoxHomeLab,
  },
  {
    slug: 'windows-ad-lab',
    title: 'Windows Server and Active Directory Lab',
    summary:
      'Two domain controllers and a member server on an isolated lab network, built hands-on alongside an AZ-802 course.',
    tags: ['Windows Server', 'Active Directory', 'Proxmox VE'],
    status: 'In Progress',
    content: windowsAdLab,
  },
  {
    slug: 'azure-entra-id-labs',
    title: 'Azure Labs Part 1: Entra ID Users, Groups, and Roles',
    summary:
      'Hands-on AZ-104 labs in my own Azure tenant: bulk user imports, nested and dynamic groups, delegated admin roles, guest access, and managing it all from PowerShell.',
    tags: ['Microsoft Entra ID', 'Azure', 'Azure RBAC', 'PowerShell', 'Az module'],
    status: 'In Progress',
    content: azureEntraIdLabs,
  },
  {
    slug: 'minecraft-bedrock-server',
    title: 'Minecraft Bedrock Server',
    summary:
      'A dedicated server for friends on Xbox, Switch, and PC, running as a systemd service in an LXC container, with my own Bash DDNS updater keeping its DNS record current.',
    tags: ['Debian', 'LXC', 'systemd', 'Bash', 'Cloudflare API', 'DNS'],
    status: 'Complete',
    content: minecraftBedrockServer,
    diagrams: [
      {
        slug: 'network',
        title: 'How players connect',
        description: 'PC and console connection paths, the home gateway, and NetherNet\'s two-phase connection.',
        src: '/images/minecraft-network-diagram.svg',
        alt: 'Diagram of how PC and console players reach the Bedrock server through Cloudflare DNS or BedrockConnect, the Xfinity gateway port forwards, and the Proxmox container, plus the TCP handshake and UDP gameplay phases',
      },
      {
        slug: 'container',
        title: 'Inside the server container',
        description: 'The systemd service, the DDNS and health check timers, the admin path, and the security layers.',
        src: '/images/minecraft-container-diagram.svg',
        alt: 'Diagram of the Bedrock container showing the systemd service, console pipe and journald, the DDNS and health check timers with their API calls, the outbound Cloudflare Tunnel admin path, and the nested security layers',
      },
    ],
  },
  {
    slug: 'minecraft-world-map',
    title: 'Live World Map and Backups',
    summary:
      'A public, zoomable map of the world, rendered hourly from consistent snapshots of the live server, with live player positions and tiered world backups every 6 hours.',
    tags: ['uNmINeD', 'Bash', 'systemd timers', 'Caddy', 'Cloudflare Tunnel', 'Proxmox VE'],
    status: 'Complete',
    content: minecraftWorldMap,
    parent: 'minecraft-bedrock-server',
    diagrams: [
      {
        slug: 'pipeline',
        title: 'How the map updates',
        description: 'The hourly snapshot and render pipeline across three machines, the every-minute player position loop, and how visitors reach the map.',
        src: '/images/minecraft-map-pipeline-diagram.svg',
        alt: 'Diagram of the world map pipeline: host timers run a snapshot script in the game server container that holds saves, copies and trims the world, and packs an archive, which the host verifies and pushes to the map container to unpack, back up, render and serve with Caddy; a second timer pipes player positions from the game server console to a marker script every minute; visitors reach Caddy through Cloudflare and an outbound Cloudflare Tunnel',
      },
    ],
  },
  {
    slug: 'minecraft-server-dashboard',
    title: 'Server Status Dashboard',
    summary:
      'A public Prometheus and Grafana dashboard showing who\'s online and for how long, all-time playtime, and server health, with a link to the live world map and Discord notifications when a player joins.',
    tags: ['Prometheus', 'Grafana', 'node_exporter', 'blackbox_exporter', 'Bash', 'Cloudflare Access'],
    status: 'Complete',
    content: minecraftServerDashboard,
    parent: 'minecraft-bedrock-server',
    diagrams: [
      {
        slug: 'data-flow',
        title: 'How the dashboard gets its data',
        description: 'The visitor path through Cloudflare, the Prometheus scrapes, the server log readers, the two port probes, and the Discord webhook.',
        src: '/images/minecraft-dashboard-diagram.svg',
        alt: 'Diagram of the status dashboard: visitors reach Grafana through Cloudflare Access and an outbound Cloudflare Tunnel, Grafana queries Prometheus, Prometheus scrapes blackbox_exporter, the Proxmox exporter and node_exporter on the game server, scripts and a Discord watcher read the game server\'s systemd journal, blackbox_exporter probes the game port directly and through the router\'s port forward, and the watcher posts joins to Discord through a webhook',
      },
    ],
  },
  {
    slug: 'photo-gallery',
    title: 'Self-Hosted Photo Gallery',
    summary:
      'Lychee and MariaDB with Docker Compose, protected by Cloudflare Access plus the app\'s own login, with private albums and a shared family album.',
    tags: ['Docker Compose', 'Lychee', 'MariaDB', 'LXC', 'Cloudflare Access'],
    status: 'Complete',
    content: photoGallery,
  },
  {
    slug: 'browser-dev-environment',
    title: 'Browser-Based Dev Environment',
    summary:
      'VS Code in the browser with code-server, running under systemd in an LXC container and published through Cloudflare Tunnel behind Cloudflare Access.',
    tags: ['code-server', 'Debian', 'LXC', 'systemd', 'Cloudflare Tunnel', 'Cloudflare Access'],
    status: 'Complete',
    content: browserDevEnvironment,
  },
  {
    slug: 'portfolio-site',
    title: 'This Site: jaxsoncodes.com',
    summary:
      'A React site on AWS Amplify, and everything it taught me about rewrite rules, CDN caching, and keeping secrets out of a public repository.',
    tags: ['React', 'TypeScript', 'Tailwind CSS', 'AWS Amplify', 'CloudFront', 'Cloudflare'],
    status: 'In Progress',
    content: portfolioSite,
  },
];

export const getProject = (slug: string | undefined) => projects.find((p) => p.slug === slug);
export const getSubProjects = (project: Project) => projects.filter((p) => p.parent === project.slug);
export const getDiagram = (project: Project | undefined, slug: string | undefined) =>
  project?.diagrams?.find((d) => d.slug === slug);
